import { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import {
    LayoutDashboard,
    BookOpen,
    Activity,
    Utensils,
    TestTube2,
    Pill,
    CalendarClock,
    FlameKindling,
    Users,
    Brain,
    MessageSquare,
    Settings,
    LogOut,
    ChevronLeft,
    ChevronRight,
    Sun,
    Moon,
    Sparkles,
    Crown,
    BedDouble,
    Clock,
    Heart,
    Mic,
    Camera,
    Images,
    Bell,
    Shield,
    Bug,
    FileDown,
    Cloud,
    Syringe,
    AlertTriangle,
    Menu,
    X
} from 'lucide-react';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from './ui/tooltip';

const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', tier: 'free' },
    { icon: Bell, label: 'News & Updates', path: '/news', tier: 'free', badge: 'news_unread' },
    { icon: BookOpen, label: 'Knowledge Base', path: '/knowledge', tier: 'essential' },
    { icon: Mic, label: 'Voice Journal', path: '/voice-journal', tier: 'essential' },
    { icon: Camera, label: 'Photo Log', path: '/photo-log', tier: 'essential' },
    { icon: Images, label: 'My Photos', path: '/my-photos', tier: 'essential' },
    { icon: Activity, label: 'Symptom Tracker', path: '/symptoms', tier: 'essential' },
    { icon: BedDouble, label: 'Sleep Tracker', path: '/sleep', tier: 'essential' },
    { icon: Utensils, label: 'Food Tracker', path: '/food', tier: 'essential' },
    { icon: Cloud, label: 'Weather Tracker', path: '/weather', tier: 'essential' },
    { icon: FlameKindling, label: 'Flare Journal', path: '/flares', tier: 'essential' },
    { icon: Heart, label: 'Cycle Tracker', path: '/cycle', tier: 'essential' },
    { icon: Syringe, label: 'Vaccine Tracker', path: '/vaccines', tier: 'essential' },
    { icon: Users, label: 'Community', path: '/community', tier: 'essential' },
    { icon: Brain, label: 'Coping Tools', path: '/coping', tier: 'essential' },
    { icon: MessageSquare, label: 'AI Chat', path: '/chat', tier: 'essential' },
    { icon: Clock, label: 'Medical Timeline', path: '/timeline', tier: 'premium' },
    { icon: TestTube2, label: 'Lab Tracker', path: '/labs', tier: 'premium' },
    { icon: Pill, label: 'Supplements & Meds', path: '/supplements', tier: 'premium' },
    { icon: Bell, label: 'Med Reminders', path: '/medication-reminders', tier: 'premium' },
    { icon: CalendarClock, label: 'Appointments', path: '/appointments', tier: 'premium' },
    { icon: FileDown, label: 'Export Data', path: '/export', tier: 'premium' },
];

const tierColors = {
    free: '',
    essential: 'text-secondary',
    premium: 'text-accent'
};

export const Sidebar = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [newsUnread, setNewsUnread] = useState(0);
    const { user, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();

    // Fetch unread news count (poll every 60s)
    useEffect(() => {
        let cancelled = false;
        const fetchUnread = async () => {
            if (!user) return;
            try {
                const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/news/unread-count`);
                if (!cancelled) setNewsUnread(data.unread || 0);
            } catch (e) { /* silent */ }
        };
        fetchUnread();
        const id = setInterval(fetchUnread, 60000);
        return () => { cancelled = true; clearInterval(id); };
    }, [user]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const closeMobileMenu = () => {
        setMobileOpen(false);
    };

    const userTier = user?.subscription_tier || 'free';
    const tierAccess = {
        free: ['free'],
        essential: ['free', 'essential'],
        premium: ['free', 'essential', 'premium']
    };

    const hasAccess = (itemTier) => tierAccess[userTier]?.includes(itemTier);

    // Sidebar content - shared between mobile and desktop
    const SidebarContent = ({ isMobile = false, onClose }) => (
        <>
            {/* Logo */}
            <div className={cn(
                "flex items-center gap-3 p-4 border-b border-border",
                collapsed && !isMobile ? "justify-center" : "justify-between"
            )}>
                {(!collapsed || isMobile) && (
                    <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h1 className="font-heading font-bold text-sm leading-tight">Autoimmune</h1>
                            <p className="text-xs text-muted-foreground">Pathways</p>
                        </div>
                    </div>
                )}
                {collapsed && !isMobile && (
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-white" />
                    </div>
                )}
                {/* Mobile close button */}
                {isMobile && onClose && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClose}
                        className="text-muted-foreground hover:text-foreground"
                    >
                        <X className="w-5 h-5" />
                    </Button>
                )}
            </div>

            {/* User Info */}
            {(!collapsed || isMobile) && (
                <div className="p-4 border-b border-border">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-accent-400 flex items-center justify-center text-white font-semibold">
                            {user?.name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{user?.name || 'User'}</p>
                            <div className="flex items-center gap-1.5">
                                <span className={cn(
                                    "text-xs capitalize font-medium",
                                    userTier === 'premium' ? 'text-accent' : 
                                    userTier === 'essential' ? 'text-secondary' : 'text-muted-foreground'
                                )}>
                                    {userTier}
                                </span>
                                {user?.is_founding_member && (
                                    <Crown className="w-3 h-3 text-amber-500" />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto p-3 space-y-1">
                {navItems.map((item) => {
                    const Icon = item.icon;
                    const isLocked = !hasAccess(item.tier);

                    const linkContent = (
                        <NavLink
                            to={isLocked ? '#' : item.path}
                            onClick={(e) => {
                                if (isLocked) {
                                    e.preventDefault();
                                } else if (isMobile && onClose) {
                                    onClose();
                                }
                            }}
                            className={({ isActive }) => cn(
                                "relative flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium text-sm",
                                "duration-200",
                                collapsed && !isMobile && "justify-center px-2",
                                isActive && !isLocked
                                    ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20"
                                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
                                isLocked && "opacity-50 cursor-not-allowed"
                            )}
                            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                            <Icon className={cn("w-5 h-5 flex-shrink-0", tierColors[item.tier])} />
                            {/* News unread badge — visible even when collapsed */}
                            {item.badge === 'news_unread' && newsUnread > 0 && collapsed && !isMobile && (
                                <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-rose-500" data-testid="sidebar-news-dot-collapsed" />
                            )}
                            {(!collapsed || isMobile) && (
                                <>
                                    <span className="flex-1">{item.label}</span>
                                    {item.badge === 'news_unread' && newsUnread > 0 && (
                                        <span className="text-xs bg-rose-500 text-white px-1.5 py-0.5 rounded-full min-w-[1.25rem] text-center" data-testid="sidebar-news-badge">
                                            {newsUnread > 9 ? '9+' : newsUnread}
                                        </span>
                                    )}
                                    {isLocked && (
                                        <span className="text-xs bg-muted px-1.5 py-0.5 rounded">
                                            {item.tier === 'premium' ? 'PRO' : 'ESS'}
                                        </span>
                                    )}
                                </>
                            )}
                        </NavLink>
                    );

                    if (collapsed && !isMobile) {
                        return (
                            <Tooltip key={item.path}>
                                <TooltipTrigger asChild>
                                    {linkContent}
                                </TooltipTrigger>
                                <TooltipContent side="right" className="flex items-center gap-2">
                                    {item.label}
                                    {isLocked && (
                                        <span className="text-xs bg-muted px-1.5 py-0.5 rounded">
                                            {item.tier === 'premium' ? 'PRO' : 'ESS'}
                                        </span>
                                    )}
                                </TooltipContent>
                            </Tooltip>
                        );
                    }

                    return <div key={item.path}>{linkContent}</div>;
                })}
            </nav>

            {/* Bottom Section */}
            <div className={cn(
                "p-3 border-t border-border space-y-1",
                collapsed && !isMobile && "px-2"
            )}>
                {/* Theme Toggle */}
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={toggleTheme}
                            className={cn(
                                "w-full justify-start gap-3 text-muted-foreground hover:text-foreground",
                                collapsed && !isMobile && "justify-center px-2"
                            )}
                            data-testid="theme-toggle"
                        >
                            {theme === 'dark' ? (
                                <Sun className="w-5 h-5" />
                            ) : (
                                <Moon className="w-5 h-5" />
                            )}
                            {(!collapsed || isMobile) && <span>Dark Mode</span>}
                        </Button>
                    </TooltipTrigger>
                    {collapsed && !isMobile && (
                        <TooltipContent side="right">Toggle Theme</TooltipContent>
                    )}
                </Tooltip>

                {/* Settings */}
                <Tooltip>
                    <TooltipTrigger asChild>
                        <NavLink
                            to="/settings"
                            onClick={() => isMobile && onClose && onClose()}
                            className={({ isActive }) => cn(
                                "flex items-center gap-3 px-3 py-2 rounded-xl text-sm",
                                "duration-200",
                                collapsed && !isMobile && "justify-center px-2",
                                isActive
                                    ? "bg-primary text-primary-foreground"
                                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                            )}
                            data-testid="nav-settings"
                        >
                            <Settings className="w-5 h-5" />
                            {(!collapsed || isMobile) && <span>Settings</span>}
                        </NavLink>
                    </TooltipTrigger>
                    {collapsed && !isMobile && (
                        <TooltipContent side="right">Settings</TooltipContent>
                    )}
                </Tooltip>

                {/* Admin Link (if admin) */}
                {user?.is_admin && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <NavLink
                                to="/admin"
                                onClick={() => isMobile && onClose && onClose()}
                                className={({ isActive }) => cn(
                                    "flex items-center gap-3 px-3 py-2 rounded-xl text-sm",
                                    "duration-200",
                                    collapsed && !isMobile && "justify-center px-2",
                                    isActive
                                        ? "bg-amber-100 dark:bg-amber-950/30 text-amber-600"
                                        : "text-muted-foreground hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-950/20"
                                )}
                                data-testid="nav-admin"
                            >
                                <Shield className="w-5 h-5" />
                                {(!collapsed || isMobile) && <span>Admin</span>}
                            </NavLink>
                        </TooltipTrigger>
                        {collapsed && !isMobile && (
                            <TooltipContent side="right">Admin Dashboard</TooltipContent>
                        )}
                    </Tooltip>
                )}

                {/* Report Issue */}
                <Tooltip>
                    <TooltipTrigger asChild>
                        <NavLink
                            to="/report-issue"
                            onClick={() => isMobile && onClose && onClose()}
                            className={({ isActive }) => cn(
                                "flex items-center gap-3 px-3 py-2 rounded-xl text-sm",
                                "duration-200",
                                collapsed && !isMobile && "justify-center px-2",
                                isActive
                                    ? "bg-rose-100 dark:bg-rose-950/30 text-rose-600"
                                    : "text-muted-foreground hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/20"
                            )}
                            data-testid="nav-report-issue"
                        >
                            <Bug className="w-5 h-5" />
                            {(!collapsed || isMobile) && <span>Report Issue</span>}
                        </NavLink>
                    </TooltipTrigger>
                    {collapsed && !isMobile && (
                        <TooltipContent side="right">Report Issue</TooltipContent>
                    )}
                </Tooltip>

                {/* Logout */}
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                if (isMobile && onClose) onClose();
                                handleLogout();
                            }}
                            className={cn(
                                "w-full justify-start gap-3 text-muted-foreground hover:text-destructive",
                                collapsed && !isMobile && "justify-center px-2"
                            )}
                            data-testid="logout-btn"
                        >
                            <LogOut className="w-5 h-5" />
                            {(!collapsed || isMobile) && <span>Logout</span>}
                        </Button>
                    </TooltipTrigger>
                    {collapsed && !isMobile && (
                        <TooltipContent side="right">Logout</TooltipContent>
                    )}
                </Tooltip>

                {/* Collapse Toggle - Desktop only */}
                {!isMobile && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCollapsed(!collapsed)}
                        className={cn(
                            "w-full justify-center text-muted-foreground hover:text-foreground mt-2"
                        )}
                        data-testid="sidebar-collapse"
                    >
                        {collapsed ? (
                            <ChevronRight className="w-5 h-5" />
                        ) : (
                            <ChevronLeft className="w-5 h-5" />
                        )}
                    </Button>
                )}

                {/* Medical Disclaimer Link */}
                <Tooltip>
                    <TooltipTrigger asChild>
                        <NavLink
                            to="/medical-disclaimer"
                            onClick={() => isMobile && onClose && onClose()}
                            className={cn(
                                "flex items-center gap-3 px-3 py-2 rounded-lg text-xs transition-colors mt-2",
                                collapsed && !isMobile && "justify-center px-2",
                                "text-muted-foreground/70 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-950/20"
                            )}
                            data-testid="nav-medical-disclaimer"
                        >
                            <AlertTriangle className="w-4 h-4" />
                            {(!collapsed || isMobile) && <span>Medical Disclaimer</span>}
                        </NavLink>
                    </TooltipTrigger>
                    {collapsed && !isMobile && (
                        <TooltipContent side="right">Medical Disclaimer</TooltipContent>
                    )}
                </Tooltip>
            </div>
        </>
    );

    return (
        <TooltipProvider delayDuration={0}>
            {/* Mobile Menu Button */}
            <Button
                variant="ghost"
                size="icon"
                className="fixed top-4 left-4 z-[60] lg:hidden bg-card/90 backdrop-blur-sm shadow-lg border border-border"
                onClick={() => setMobileOpen(!mobileOpen)}
                data-testid="mobile-menu-toggle"
            >
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>

            {/* Mobile Overlay */}
            {mobileOpen && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[51] lg:hidden"
                    onClick={closeMobileMenu}
                    onTouchEnd={closeMobileMenu}
                />
            )}

            {/* Mobile Sidebar */}
            <aside
                className={cn(
                    "fixed top-0 left-0 h-screen z-[55] bg-card border-r border-border flex flex-col",
                    "transform transition-transform duration-300 ease-in-out lg:hidden",
                    mobileOpen ? "translate-x-0" : "-translate-x-full",
                    "w-[280px]"
                )}
                data-testid="mobile-sidebar"
            >
                <SidebarContent isMobile={true} onClose={closeMobileMenu} />
            </aside>

            {/* Desktop Sidebar */}
            <aside
                className={cn(
                    "hidden lg:flex h-screen sticky top-0 flex-col border-r border-border bg-card/50 backdrop-blur-xl",
                    "duration-300",
                    collapsed ? "w-[72px]" : "w-[260px]"
                )}
                data-testid="sidebar"
            >
                <SidebarContent isMobile={false} />
            </aside>
        </TooltipProvider>
    );
};

export default Sidebar;
